import {
  createContext,
  createEffect,
  For,
  createRoot,
  createSignal,
  getOwner,
  onCleanup,
  type Owner,
  type ParentProps,
  runWithOwner,
  useContext,
  type JSX,
} from "solid-js"
import { Dialog as Kobalte } from "@kobalte/core/dialog"
import { makeEventListener } from "@solid-primitives/event-listener"

// Overlays that close themselves on Escape (popover.tsx, Kobalte menus and
// selects). Only mounted while open (or closing).
const OPEN_OVERLAY = [
  '[data-component="popover-content"]',
  '[data-component="dropdown-menu-content"]',
  '[data-component="context-menu-content"]',
  '[data-component="select-content"]',
].join(", ")

type DialogElement = () => JSX.Element

type Active = {
  id: string
  node: JSX.Element
  dispose: () => void
  owner: Owner
  onClose?: () => void
  setClosing: (closing: boolean) => void
}

const Context = createContext<ReturnType<typeof init>>()

function init() {
  const [stack, setStack] = createSignal<Active[]>([])
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const closing = new Set<string>()

  onCleanup(() => {
    for (const timer of timers.values()) clearTimeout(timer)
    timers.clear()
    for (const item of stack()) item.dispose()
    setStack([])
  })

  const active = () => stack().at(-1)

  const dismiss = (current: Active) => {
    closing.add(current.id)
    current.onClose?.()
    current.setClosing(true)

    const targetId = current.id
    timers.set(
      targetId,
      setTimeout(() => {
        timers.delete(targetId)
        closing.delete(targetId)
        current.dispose()
        setStack((items) => items.filter((item) => item.id !== targetId))
        // Hand focus back to the dialog underneath. Kobalte restores focus to
        // whatever opened the closing dialog only within its own root, and a
        // nested dialog is a separate root — so without this, closing the inner
        // one drops focus onto <body> and the keyboard user is outside the
        // dialog that is still on screen. (Before only-the-top-is-modal, the
        // outer trap yanked focus back by force; that is the behaviour being
        // replaced, not lost.)
        queueMicrotask(() => {
          // Keyed off the top dialog's own id, not "the last content node in the
          // DOM": a dialog closing with a transition is still mounted, so DOM
          // order can put a disappearing dialog after the one that should get
          // the focus back.
          const top = active()
          if (!top || closing.has(top.id)) return
          const scope = document.querySelector<HTMLElement>(`[data-dialog-id="${top.id}"]`)
          scope?.querySelector<HTMLElement>('[data-slot="dialog-content"]')?.focus?.()
        })
      }, 100),
    )
  }

  const close = (id?: string) => {
    const current = id ? stack().find((item) => item.id === id) : active()
    if (!current || closing.has(current.id)) return
    if (id && active()?.id !== id) return
    dismiss(current)
  }

  // Dismisses the whole stack, not just the top. Needed by anything that
  // navigates away from behind the dialogs: the archive can be opened from
  // inside Settings, and popping one level there lands the user on a chat that
  // a still-modal Settings dialog is covering. close(id) deliberately refuses
  // to skip a level, so this is its own entry point rather than a loop over it.
  const closeAll = () => {
    for (const item of stack()) {
      if (closing.has(item.id)) continue
      dismiss(item)
    }
  }

  createEffect(() => {
    if (stack().length === 0) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      // A popover, menu or select open above the dialog owns this Escape and
      // closes itself; the dialog under it stays. Both listen on window in the
      // capture phase and this one was registered first, so the overlay's own
      // stopPropagation could not help: Esc on a «?» in Settings closed
      // Settings (30.09).
      if (document.querySelector(OPEN_OVERLAY)) return
      close()
      event.preventDefault()
      event.stopPropagation()
    }

    makeEventListener(window, "keydown", onKeyDown, { capture: true })
  })

  const show = (element: DialogElement, owner: Owner, onClose?: () => void) => {
    const id = Math.random().toString(36).slice(2)
    let dispose: (() => void) | undefined
    let setClosing: ((closing: boolean) => void) | undefined

    const node = runWithOwner(owner, () =>
      createRoot((d: () => void) => {
        dispose = d
        const [closing, setClosingSignal] = createSignal(false)
        setClosing = setClosingSignal
        // Only the TOP dialog is modal. Every dialog in the stack stays mounted,
        // and a Kobalte modal dialog installs a focus trap plus aria-hidden on
        // everything outside its own content node — which, for a nested dialog,
        // includes the nested dialog itself (each one is its own portal). Two
        // live traps then fight over every focus change: the outer one pulls
        // focus back, the inner one's autofocus grabs it again, and the only
        // usable field in the inner dialog is the autofocused one.
        //
        // That is not theoretical. In Settings → «MCP и коннекторы» → «Яндекс
        // Почта», clicking the app-password field (or tabbing to it) left the
        // caret in the address field and typed the password into it — reported
        // verbatim as «курсор всегда находится в поле названия почты и поле кода
        // недоступно», with the connector impossible to complete. Every nested
        // dialog in the app had it.
        const isTop = () => active()?.id === id
        return (
          <Kobalte
            modal={isTop()}
            open={!closing()}
            onOpenChange={(open: boolean) => {
              if (open) return
              close(id)
            }}
          >
            <Kobalte.Portal>
              {/* `display: contents` — a marker for "which dialog is this?" that
                  takes part in no layout. Focus restoration needs to find one
                  dialog's content node by id, and the nodes themselves carry
                  nothing that tells them apart. */}
              <div data-dialog-id={id} style={{ display: "contents" }}>
                <Kobalte.Overlay data-component="dialog-overlay" onClick={() => close(id)} />
                {element()}
              </div>
            </Kobalte.Portal>
          </Kobalte>
        )
      }),
    )

    const rootDispose = dispose
    const updateClosing = setClosing
    if (!rootDispose || !updateClosing) return

    setStack((items) => [...items, { id, node, dispose: rootDispose, owner, onClose, setClosing: updateClosing }])
  }

  return {
    get active() {
      return active()
    },
    get stack() {
      return stack()
    },
    close,
    closeAll,
    show,
  }
}

export function DialogProvider(props: ParentProps) {
  const ctx = init()
  return (
    <Context.Provider value={ctx}>
      {props.children}
      <div data-component="dialog-stack">
        <For each={ctx.stack}>{(item) => item.node}</For>
      </div>
    </Context.Provider>
  )
}

export function useDialog() {
  const ctx = useContext(Context)
  const owner = getOwner()

  if (!owner) {
    throw new Error("useDialog must be used within a DialogProvider")
  }
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider")
  }

  return {
    get active() {
      return ctx.active
    },
    show(element: DialogElement, onClose?: () => void) {
      const base = ctx.active?.owner ?? owner
      ctx.show(element, base, onClose)
    },
    close() {
      ctx.close()
    },
    closeAll() {
      ctx.closeAll()
    },
  }
}
